// src/components/Questionnaires/OnlineCoursePlan.js

import React, {useEffect, useState} from 'react';
import {Card, Form, Radio, Checkbox, Input, Button, Alert, Typography, message, Spin} from 'antd';
import moment from 'moment';
import "./OnlineCoursePlan.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const OnlineCoursePlan = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/online-course-plan/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/online-course-plan/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Helper function to transform form values
    const transformValues = (values) => {
        const transformed = {};
        for (const [key, value] of Object.entries(values)) {
            if (Array.isArray(value)) {
                transformed[key] = value.join(', '); // Convert array to comma-separated string
            } else {
                transformed[key] = value;
            }
        }
        return transformed;
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const transformedAnswers = transformValues(values);
            const response = await fetch(`${server_url}/questionnaires/online-course-plan/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: transformedAnswers, // Use transformed answers
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to submit Online Course Plan.');
            }

            const data = await response.json();
            message.success('Online Course Plan submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Online Course Plan:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>
            <div className="sfl-container">
                <h2 className="sfl-header">PART A (6) - Skills for Life</h2>

                <p className="sfl-description">
                    We develop lots of important skills in our school years. Some of them are useful in school, but some
                    of them are <strong>“life skills”</strong> that will be important to us in the future, no matter
                    what we do. Think about one important life skill that you have gained and fill out the boxes below.
                </p>

                <table className="sfl-table">
                    <tr className="sfl-tr-orange">
                        <th className="sfl-th-orange">
                            One important life skill you have learned at home, school, or in the community
                        </th>
                        <th className="sfl-th-orange">
                            Experiences at home, school, or in the community that helped you develop the skill
                        </th>
                    </tr>
                    <tr>
                        <td className="sfl-td-orange">
                            <textarea className="sfl-textarea"></textarea>
                        </td>
                        <td className="sfl-td-orange">
                            <textarea className="sfl-textarea"></textarea>
                        </td>
                    </tr>
                </table>

                <div className="sfl-skill-important">
                    <h4 className="sfl-skill-important-header">Why is this skill important to you?</h4>
                    <textarea className="sfl-skill-important-textarea"></textarea>
                </div>

                <div className="sfl-other-skill">
                    <h4 className="sfl-other-skill-header">
                        What is another life skill you would like to develop and why do you think it will be important
                        to you?
                    </h4>
                    <textarea className="sfl-other-skill-textarea"></textarea>
                </div>

                {/*<h2 className="sfl-online-header">Plan to do Online Courses</h2>*/}

                <p className="sfl-online-desc1">
                    As part of your upskilling at this stage of school education, you are encouraged to do online
                    courses and tutorials that help you develop different competencies. Given below is a list of
                    possible areas you could do some research on before choosing courses that will be of interest.
                </p>

                <p className="sfl-online-desc2">
                    At the beginning of the year, start planning courses you would like to do over the course of the
                    year.
                </p>

                <div className="sfl-online-tip">
                    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2"
                         stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.1" y2="16"/>
                    </svg>
                    <p className="sfl-online-tip-text">
                        Be sure to check with your teacher about which courses are suitable.
                    </p>
                </div>

                <div className="sfl-online-grid">
                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Public Speaking &amp; Storytelling
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            The Art of Formal &amp; Creative Writing
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Health &amp; Nutrition Literacy
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Gender Sensitivity
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Environmental Sustainability
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Citizen Politics in Democracy
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Mental Wellness
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Ethics &amp; Reasoning
                        </text>
                    </svg>

                    <svg className="sfl-course-svg" width="100%" height="100" viewBox="0 0 200 100"
                         onClick="toggleSelection(event)">
                        <rect width="200" height="50" fill="#2ecc71" stroke="#000" stroke-width="2"/>
                        <text x="100" y="30" fill="white" font-size="12" font-weight="bold" text-anchor="middle">
                            Artistic Literacy
                        </text>
                    </svg>
                </div>
            </div>
            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{marginBottom: 16}}*/}
            {/*/>*/}

            {/* If Eligible, Show the Form */}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* Part 1: Interest Identification *!/*/}
            {/*        <Title level={4}>Part 1: Interest Identification</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Which of the following areas interest you the most for taking an online course? (Select all that apply)"*/}
            {/*            name="Which of the following areas interest you the most for taking an online course? (Select all that apply)"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="Public Speaking and Storytelling">Public Speaking and*/}
            {/*                    Storytelling</Checkbox>*/}
            {/*                <Checkbox value="The Art of Formal and Creative Writing">The Art of Formal and Creative*/}
            {/*                    Writing</Checkbox>*/}
            {/*                <Checkbox value="Health and Nutrition Literacy">Health and Nutrition Literacy</Checkbox>*/}
            {/*                <Checkbox value="Mental Wellness">Mental Wellness</Checkbox>*/}
            {/*                <Checkbox value="Ethics and Reasoning">Ethics and Reasoning</Checkbox>*/}
            {/*                <Checkbox value="Values, Ethics, and Fundamental Duties">Values, Ethics, and Fundamental*/}
            {/*                    Duties</Checkbox>*/}
            {/*                <Checkbox value="Citizenship Values">Citizenship Values</Checkbox>*/}
            {/*                <Checkbox value="Gender Sensitivity">Gender Sensitivity</Checkbox>*/}
            {/*                <Checkbox value="Environmental Sustainability">Environmental Sustainability</Checkbox>*/}
            {/*                <Checkbox value="Citizen Politics in Democracy">Citizen Politics in Democracy</Checkbox>*/}
            {/*                <Checkbox value="Fundamentals of Neuroscience">Fundamentals of Neuroscience</Checkbox>*/}
            {/*                <Checkbox value="Artistic Literacy">Artistic Literacy</Checkbox>*/}
            {/*                <Checkbox value="Computational Thinking">Computational Thinking</Checkbox>*/}
            {/*                <Checkbox value="Coding">Coding</Checkbox>*/}
            {/*                <Checkbox value="Digital Marketing">Digital Marketing</Checkbox>*/}
            {/*                <Checkbox value="Entrepreneurship">Entrepreneurship</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 2: Course Suitability *!/*/}
            {/*        <Title level={4}>Part 2: Course Suitability</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Why do you think learning this topic (or topics) will benefit you?"*/}
            {/*            name="Why do you think learning this topic (or topics) will benefit you?"*/}
            {/*            rules={[{required: true, message: 'Please provide your reasons.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="e.g., Improving communication skills, gaining creative writing expertise, understanding environmental issues, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you feel that these courses align with your academic or career goals? If yes, how?"*/}
            {/*            name="Do you feel that these courses align with your academic or career goals? If yes, how?"*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Conditional Field: Platforms *!/*/}
            {/*        <Form.Item*/}
            {/*            shouldUpdate={(prevValues, currentValues) => prevValues["Do you feel that these courses align with your academic or career goals? If yes, how?"] !== currentValues["Do you feel that these courses align with your academic or career goals? If yes, how?"]}>*/}
            {/*            {({getFieldValue}) => {*/}
            {/*                return getFieldValue("Do you feel that these courses align with your academic or career goals? If yes, how?") === "Yes" ? (*/}
            {/*                    <Form.Item*/}
            {/*                        label="Have you researched or identified any online platforms that offer courses in these areas?"*/}
            {/*                        name="Have you researched or identified any online platforms that offer courses in these areas?"*/}
            {/*                        rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*                    >*/}
            {/*                        <Radio.Group>*/}
            {/*                            <Radio value="Yes">Yes</Radio>*/}
            {/*                            <Radio value="No">No</Radio>*/}
            {/*                        </Radio.Group>*/}
            {/*                    </Form.Item>*/}
            {/*                ) : null;*/}
            {/*            }}*/}
            {/*        </Form.Item>*/}

            {/*        /!* Conditional Field: Platforms List *!/*/}
            {/*        <Form.Item*/}
            {/*            shouldUpdate={(prevValues, currentValues) => prevValues["Have you researched or identified any online platforms that offer courses in these areas?"] !== currentValues["Have you researched or identified any online platforms that offer courses in these areas?"]}>*/}
            {/*            {({getFieldValue}) => {*/}
            {/*                return getFieldValue("Have you researched or identified any online platforms that offer courses in these areas?") === "Yes" ? (*/}
            {/*                    <Form.Item*/}
            {/*                        label="If yes, mention the platforms:"*/}
            {/*                        name="If yes, mention the platforms:"*/}
            {/*                        rules={[{required: true, message: 'Please mention the platforms.'}]}*/}
            {/*                    >*/}
            {/*                        <Input placeholder="e.g., Coursera, Udemy, edX, etc."/>*/}
            {/*                    </Form.Item>*/}
            {/*                ) : null;*/}
            {/*            }}*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 3: Readiness for Online Learning *!/*/}
            {/*        <Title level={4}>Part 3: Readiness for Online Learning</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How comfortable are you with self-paced online learning?"*/}
            {/*            name="How comfortable are you with self-paced online learning?"*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Not Comfortable">Not Comfortable</Radio>*/}
            {/*                <Radio value="Somewhat Comfortable">Somewhat Comfortable</Radio>*/}
            {/*                <Radio value="Very Comfortable">Very Comfortable</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you have access to the required resources (like internet, a device, and time) to take online courses?"*/}
            {/*            name="Do you have access to the required resources (like internet, a device, and time) to take online courses?"*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are you willing to dedicate time each week to complete an online course? If yes, how many hours per week can you commit?"*/}
            {/*            name="Are you willing to dedicate time each week to complete an online course? If yes, how many hours per week can you commit?"*/}
            {/*            rules={[{required: true, message: 'Please specify your weekly commitment.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., 2-3 hours, 4-5 hours, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 4: Setting Goals *!/*/}
            {/*        <Title level={4}>Part 4: Setting Goals</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What do you hope to achieve by completing an online course in your chosen area(s)?"*/}
            {/*            name="What do you hope to achieve by completing an online course in your chosen area(s)?"*/}
            {/*            rules={[{required: true, message: 'Please describe your goals.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="e.g., Skill enhancement, gaining certification, improving knowledge, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will you measure your success after completing the course?"*/}
            {/*            name="How will you measure your success after completing the course?"*/}
            {/*            rules={[{required: true, message: 'Please describe how you will measure success.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="e.g., Applying new skills, using the certificate for academic or career growth, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 5: Implementation *!/*/}
            {/*        <Title level={4}>Part 5: Implementation</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you discussed your interest in online courses with your teacher or mentor for guidance?"*/}
            {/*            name="Have you discussed your interest in online courses with your teacher or mentor for guidance?"*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you have a plan in place to start one of these courses within the next month or quarter?"*/}
            {/*            name="Do you have a plan in place to start one of these courses within the next month or quarter?"*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Reflection Section (Optional) *!/*/}
            {/*        <Title level={4}>Reflection Section (Optional)</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="If you’ve already taken any online courses, what was your experience like?"*/}
            {/*            name="If you’ve already taken any online courses, what was your experience like?"*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="Consider sharing what you learned, how it helped, and what could have been better."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Which of the listed skills do you think is most essential for students to learn today, and why?"*/}
            {/*            name="Which of the listed skills do you think is most essential for students to learn today, and why?"*/}
            {/*            rules={[{required: true, message: 'Please describe the essential skills.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="e.g., Critical thinking, digital literacy, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default OnlineCoursePlan;
